/**
 * THERAPIST NOTES SERVICE - Facade
 *
 * Re-exporta los sub-servicios especializados manteniendo compatibilidad
 * con los consumidores existentes.
 *
 * Sub-servicios:
 * - therapist-notes-crud.service.ts: CRUD, notebooks, chunking
 * - therapist-notes-search.service.ts: Búsqueda RAG y simple
 * - therapist-notes-ontology.service.ts: Ontología personal
 * - therapist-notes-settings.service.ts: Settings, carpetas, métricas
 */

import { therapistNotesCrudService } from './therapist-notes-crud.service';
import { therapistNotesSearchService } from './therapist-notes-search.service';
import { therapistNotesOntologyService } from './therapist-notes-ontology.service';
import { therapistNotesSettingsService } from './therapist-notes-settings.service';

/**
 * Facade que mantiene la API original delegando a sub-servicios.
 * Los consumidores existentes pueden seguir usando `therapistNotesService.*`
 * sin cambios.
 */
class TherapistNotesService {
  // CRUD
  createNote = therapistNotesCrudService.createNote.bind(therapistNotesCrudService);
  getNotes = therapistNotesCrudService.getNotes.bind(therapistNotesCrudService);
  getNote = therapistNotesCrudService.getNote.bind(therapistNotesCrudService);
  updateNote = therapistNotesCrudService.updateNote.bind(therapistNotesCrudService);
  deleteNote = therapistNotesCrudService.deleteNote.bind(therapistNotesCrudService);

  // Notebooks
  createNotebook = therapistNotesCrudService.createNotebook.bind(therapistNotesCrudService);
  addPageToNotebook = therapistNotesCrudService.addPageToNotebook.bind(therapistNotesCrudService);
  deletePageFromNotebook = therapistNotesCrudService.deletePageFromNotebook.bind(therapistNotesCrudService);
  updatePageText = therapistNotesCrudService.updatePageText.bind(therapistNotesCrudService);
  reorderPages = therapistNotesCrudService.reorderPages.bind(therapistNotesCrudService);

  // Search
  searchChunks = therapistNotesSearchService.searchChunks.bind(therapistNotesSearchService);
  searchNotes = therapistNotesSearchService.searchNotes.bind(therapistNotesSearchService);

  // Ontology
  getOntology = therapistNotesOntologyService.getOntology.bind(therapistNotesOntologyService);
  addOntologyNode = therapistNotesOntologyService.addOntologyNode.bind(therapistNotesOntologyService);
  updateOntologySettings = therapistNotesOntologyService.updateOntologySettings.bind(therapistNotesOntologyService);

  // Settings & Folders
  getUsageMetrics = therapistNotesSettingsService.getUsageMetrics.bind(therapistNotesSettingsService);
  checkQueryQuota = therapistNotesSettingsService.checkQueryQuota.bind(therapistNotesSettingsService);
  recordQuery = therapistNotesSettingsService.recordQuery.bind(therapistNotesSettingsService);
  getNotesStats = therapistNotesSettingsService.getNotesStats.bind(therapistNotesSettingsService);
  getNotesSettings = therapistNotesSettingsService.getNotesSettings.bind(therapistNotesSettingsService);
  getAvailableCategories = therapistNotesSettingsService.getAvailableCategories.bind(therapistNotesSettingsService);
  getAvailableTags = therapistNotesSettingsService.getAvailableTags.bind(therapistNotesSettingsService);
  addCustomCategory = therapistNotesSettingsService.addCustomCategory.bind(therapistNotesSettingsService);
  addCustomTag = therapistNotesSettingsService.addCustomTag.bind(therapistNotesSettingsService);
  removeCustomCategory = therapistNotesSettingsService.removeCustomCategory.bind(therapistNotesSettingsService);
  removeCustomTag = therapistNotesSettingsService.removeCustomTag.bind(therapistNotesSettingsService);
  hideTag = therapistNotesSettingsService.hideTag.bind(therapistNotesSettingsService);
  showTag = therapistNotesSettingsService.showTag.bind(therapistNotesSettingsService);
  updatePreferences = therapistNotesSettingsService.updatePreferences.bind(therapistNotesSettingsService);
  getFolders = therapistNotesSettingsService.getFolders.bind(therapistNotesSettingsService);
  createFolder = therapistNotesSettingsService.createFolder.bind(therapistNotesSettingsService);
  updateFolder = therapistNotesSettingsService.updateFolder.bind(therapistNotesSettingsService);
  deleteFolder = therapistNotesSettingsService.deleteFolder.bind(therapistNotesSettingsService);
  moveNoteToFolder = therapistNotesSettingsService.moveNoteToFolder.bind(therapistNotesSettingsService);
  getNotesFiltered = therapistNotesSettingsService.getNotesFiltered.bind(therapistNotesSettingsService);
  getTagsSummary = therapistNotesSettingsService.getTagsSummary.bind(therapistNotesSettingsService);
  getCategoriesSummary = therapistNotesSettingsService.getCategoriesSummary.bind(therapistNotesSettingsService);
}

export const therapistNotesService = new TherapistNotesService();

// Re-export sub-services para uso directo cuando se prefiera
export { therapistNotesCrudService } from './therapist-notes-crud.service';
export { therapistNotesSearchService } from './therapist-notes-search.service';
export { therapistNotesOntologyService } from './therapist-notes-ontology.service';
export { therapistNotesSettingsService } from './therapist-notes-settings.service';
